import { useMemo, useState } from "react";
import { Outlet } from "react-router";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import logo from "@/assets/logo/logo_colour.jpeg";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { ErrorBoundary } from "react-error-boundary";
import { ErrorFallback } from "@/components/ui/ErrorFallback";
import type { Email } from "@/types";

type EmailTab = "INBOUND" | "OUTBOUND" | "DRAFT";

type EmailSidebarProps = {
  activeTab: EmailTab;
  filteredEmails: Email[];
  searchTerm: string;
  selectedEmailId: string | null;
  onSelectEmail: (emailId: string) => void;
  onSearchChange: (searchTerm: string) => void;
  onTabChange: (tab: EmailTab) => void;
};

const EmailSidebar = ({
  activeTab,
  filteredEmails,
  searchTerm,
  selectedEmailId,
  onSelectEmail,
  onSearchChange,
  onTabChange,
}: EmailSidebarProps) => (
  <div className="flex h-full min-w-0 flex-col bg-white">
    <Link to="/app" className="flex items-center gap-3 p-4">
      <img
        src={logo}
        alt="Tyto Bio Logo"
        className="h-8 w-8 shrink-0 object-contain"
      />
      <div className="min-w-0">
        <div className="truncate text-xl font-semibold tracking-tight text-primary">Tyto Bio</div>
        <div className="text-sm font-light text-muted-foreground">Email</div>
      </div>
    </Link>
    <div className="px-4 pb-3">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          type="search"
          placeholder="Search emails..."
          value={searchTerm}
          className="pl-9"
          onChange={(event) => onSearchChange(event.target.value)}
        />
      </div>
    </div>
    <Tabs
      value={activeTab}
      onValueChange={(value) => onTabChange(value as EmailTab)}
      className="w-full px-4"
    >
      <TabsList className="grid w-full grid-cols-3">
        <TabsTrigger value="INBOUND">Inbox</TabsTrigger>
        <TabsTrigger value="OUTBOUND">Sent</TabsTrigger>
        <TabsTrigger value="DRAFT">Draft</TabsTrigger>
      </TabsList>
    </Tabs>
    <div className="min-h-0 flex-1">
      <ScrollArea className="h-full">
        <div className="space-y-2 p-4">
          {filteredEmails.length === 0 ? (
            <div className="pt-8 text-center text-sm text-muted-foreground">
              No emails found
            </div>
          ) : (
            filteredEmails.map((email) => (
              <button
                key={email.id}
                type="button"
                className={cn(
                  "w-full cursor-pointer rounded-lg border p-3 text-left transition-colors",
                  email.id === selectedEmailId
                    ? "bg-accent"
                    : "hover:bg-muted",
                  !email.parsed && "border-l-4 border-l-primary"
                )}
                onClick={() => onSelectEmail(email.id)}
              >
                <div className="flex min-w-0 items-start justify-between gap-3">
                  <span className="min-w-0 truncate text-sm font-medium">{email.subject}</span>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {new Date(email.createdAt).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                </div>
                <div className="mt-1 truncate text-xs text-muted-foreground">
                  {email.subject}
                </div>
              </button>
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  </div>
);

export const EmailLayout = () => {
  const [activeTab, setActiveTab] = useState<EmailTab>("OUTBOUND");
  const [selectedEmailId, setSelectedEmailId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  const { data: emails = [] } = useQuery({
    queryKey: ["emails"],
    queryFn: async () => {
      const response = await fetch(`/api/email`);
      const data = await response.json() as Email[]
      return data;
    },
  });


  const filteredEmails = useMemo(() => {
    const normalizedSearchTerm = searchTerm.trim().toLowerCase();

    return emails.filter((email) => {
      const matchesSearch = normalizedSearchTerm.length === 0
        || email.subject.toLowerCase().includes(normalizedSearchTerm);

      return email.direction === activeTab && matchesSearch;
    });
  }, [activeTab, emails, searchTerm]);

  const outletContext = { emails, selectedEmailId, setSelectedEmailId };
  const sidebarProps = {
    activeTab,
    filteredEmails,
    searchTerm,
    selectedEmailId,
    onSelectEmail: setSelectedEmailId,
    onSearchChange: setSearchTerm,
    onTabChange: setActiveTab,
  };

  return (
    <div className="flex h-dvh w-full flex-col overflow-hidden bg-white">
      <div className="min-h-0 flex-1 overflow-hidden md:hidden">
        <div className={cn("h-full", selectedEmailId && "hidden")}>
          <EmailSidebar {...sidebarProps} />
        </div>
        <div className={cn("h-full", !selectedEmailId && "hidden")}>
          <ErrorBoundary
            fallbackRender={({ error }) => <ErrorFallback error={error} />}
          >
            <Outlet context={outletContext} />
          </ErrorBoundary>
        </div>
      </div>
      <div className="hidden min-h-0 flex-1 overflow-hidden md:block">
        <ResizablePanelGroup direction="horizontal" className="h-full">
          <ResizablePanel defaultSize={25} minSize={20} maxSize={40}>
            <EmailSidebar {...sidebarProps} />
          </ResizablePanel>
          <ResizableHandle />
          <ResizablePanel defaultSize={75}>
            <div className="flex h-full min-w-0 flex-col">
              <ErrorBoundary
                fallbackRender={({ error }) => <ErrorFallback error={error} />}
              >
                <Outlet context={outletContext} />
              </ErrorBoundary>
            </div>
          </ResizablePanel>
        </ResizablePanelGroup >
      </div>
    </div>
  );
};

export default EmailLayout;
